import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { QueryProvider } from '@thd-nucleus/data-sources';
import {
  Alert,
  TileGroup, Typography,
  Drawer,
  DrawerBody,
  DrawerHeader,
  Skeleton,
  SkeletonLine,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  DROPDOWN_CONTAINER_TITLE,
  HAULAWAY_NOT_QUALIFY,
  HEADER_TITLES,
  INSTALL_NOT_AVAILABLE,
  PROTECTION_EMPTY,
  BOPIS_ALERT,
  ADD_ON_CATEGORIES, ADD_ON_TYPES,
  PAGE_TYPE
} from '../constants';
import { DropDownButton } from './subcomponents/DropDownButton';
import { PartsAndServices } from './PartsAndServices';
import { partsAndServicesDataModel } from './partsAndServicesDataModel';
import {
  getAddOns,
  getProducts,
  hasUserDeclinedProtection,
  hasUserDeclinedHaulAwayAndMove,
  hasUserDeclinedInstallation,
  isExcludedProduct
} from '../utils';
import { usePartsAndServicesHook } from '../hooks/usePartsAndServicesHook';
import { track } from './../parts-and-services-analytics';

const TITLE_MAP = {
  protection: HEADER_TITLES.PROTECTION,
  services: HEADER_TITLES.SERVICES,
  haulAway: HEADER_TITLES.SERVICES
};

export const PartsAndServicesDropDownContainer = ({
  itemId,
  lineItemId,
  deliveryZip,
  selectedAddOns,
  setSelectedAddOns,
  productInfo,
  isBopisAndPickup
}) => {
  const [showLoadingSkeleton, setShowLoadingSkeleton] = useState(true);
  const { isCustomerIdentified = false } = useThdCustomer() || {};
  const [drawerPageType, setDrawerPageType] = useState('');
  const virtualAssistantCategories = useConfigService('virtualAssistantCategories') || '';

  if (isExcludedProduct(productInfo, virtualAssistantCategories)) {
    return null;
  }

  const drawerTitle = TITLE_MAP[drawerPageType] || '';

  const { data } = usePartsAndServicesHook({
    itemIds: [itemId],
    storeZip: deliveryZip,
    isCustomerIdentified
  });

  const products = useMemo(() => getProducts(data), [data]);

  const productsMap = useMemo(() => {
    const map = new Map();
    if (products) {
      map.set(lineItemId, products.find((product) => product.itemId === itemId));
    }
    return map;
  }, [products, lineItemId, itemId]);

  const protectionItems = useMemo(() => (
    getAddOns({
      productsMap,
      lineItemId,
      addOnCategories: ADD_ON_CATEGORIES.PROTECTION
    })?.sort((first, second) => second?.termLength - first?.termLength)
  ), [productsMap, lineItemId]);

  const haulAwayItems = useMemo(() => (
    getAddOns({
      productsMap,
      lineItemId,
      addOnCategories: ADD_ON_CATEGORIES.HAUL_AWAY,
      addOnTypes: ADD_ON_TYPES.HAUL_AWAY
    })?.sort((first, second) => first?.price - second?.price)
  ), [productsMap, lineItemId]);

  const installItems = useMemo(() => (
    getAddOns({
      productsMap,
      lineItemId,
      addOnCategories: ADD_ON_CATEGORIES.INSTALLATION
    })
  ), [productsMap, lineItemId]);

  useEffect(() => {
    // When data arrives, stop showing the skeleton
    if (!installItems && !haulAwayItems && !protectionItems) {
      return;
    }

    track('page-load');
    setShowLoadingSkeleton(false);
  }, [installItems, haulAwayItems, protectionItems]);

  const getUserSelections = ({
    hdppSelection,
    instalSelections: installationSelections,
    haulAndMoveSelections: haulAwaySelections,
    currentPage
  }) => {
    const updates = {};

    if (currentPage === PAGE_TYPE.PROTECTION) {
      const selectedProtection = hdppSelection?.[lineItemId];

      if (hasUserDeclinedProtection(selectedProtection)) {
        // User declined - don't store in state (will use default)
        updates.protection = null;
      } else {
        // User selected protection
        updates.protection = selectedProtection;
      }
    }

    if (currentPage === PAGE_TYPE.SERVICES || currentPage === PAGE_TYPE.HAUL_AWAY) {
      const selectedInstallationSelections = installationSelections?.[lineItemId];
      const selectedHaulAwaySelections = haulAwaySelections?.[lineItemId];

      const hasInstallationAcceptanceSignal = !!selectedInstallationSelections?.some((item) => item?.installation)
        || !!selectedInstallationSelections?.some((item) => `${item?.configAttr || ''}`.toUpperCase() === 'HOOKUP');

      const isSelfInstallation = !hasInstallationAcceptanceSignal;
      const isSelfInstallDecline = isSelfInstallation
        && hasUserDeclinedInstallation(selectedInstallationSelections);

      // Store installation if user selected something, otherwise clear it (null = use default)
      updates.installation = isSelfInstallDecline ? null : selectedInstallationSelections;

      // Store haulAway if user selected something, otherwise clear it (null = use default)
      updates.haulAway = hasUserDeclinedHaulAwayAndMove(selectedHaulAwaySelections)
        ? null
        : selectedHaulAwaySelections;
    }

    // Update state with immutable pattern - only update this lineItemId
    if (Object.keys(updates).length > 0) {
      setSelectedAddOns(prev => ({
        ...prev,
        [lineItemId]: {
          ...prev[lineItemId],
          ...updates
        }
      }));
    }
  };

  // Get user selections from parent state
  const userSelections = selectedAddOns?.[lineItemId] || {};

  // Widget selections: only actual user selections, NOT defaults
  const widgetAddOnsSelections = {
    protection: {
      [lineItemId]: userSelections.protection?.configAttr || ''
    },
    installation: {
      [lineItemId]: (userSelections.installation || []).map((item) => item?.configAttr).filter(Boolean)
    },
    haulAway: {
      [lineItemId]: (userSelections.haulAway || []).map((item) => item?.configAttr).filter(Boolean)
    }
  };

  const LoadingSkeleton = () => {
    return (
      <div className="sui-mt-5 sui-mb-5">
        <Skeleton disablePadding grow>
          <SkeletonLine variant="multi" numberOfLines={2} />
        </Skeleton>
      </div>
    );
  };

  return (
    <div className="sui-flex sui-justify-between sui-bg-primary
      sui-gap-4 sui-p-3 sui-flex-wrap sui-flex-col sm:sui-flex-row sui-w-full"
    >
      <Typography variant="h4">{DROPDOWN_CONTAINER_TITLE}</Typography>
        {isBopisAndPickup && (
          <div className="sui-w-full">
            <Alert headline="Unavailable with Pickup" status="warning">{BOPIS_ALERT}</Alert>
          </div>
        )}
      <TileGroup
        aria-label="parts and services options"
        disableExclusive
        className="sui-flex sui-flex-col sui-gap-4 sui-font-bold sui-w-full"
      >
        {showLoadingSkeleton
          ? <LoadingSkeleton />
          : (
            <>
              {installItems?.length > 0
                ? (
                  <DropDownButton
                    type="installation"
                    userSelection={userSelections.installation}
                    availableItems={installItems}
                    onClick={setDrawerPageType}
                    isBopisAndPickup={isBopisAndPickup}
                  />
                )
                : <Alert status="warning">{INSTALL_NOT_AVAILABLE}</Alert>}
            </>
          )}
        {showLoadingSkeleton
          ? <LoadingSkeleton />
          : (
            <>
              {haulAwayItems?.length > 0
                ? (
                  <DropDownButton
                    type="haulAway"
                    userSelection={userSelections.haulAway}
                    availableItems={haulAwayItems}
                    onClick={setDrawerPageType}
                    isBopisAndPickup={isBopisAndPickup}
                  />
                )
                : <Alert status="warning">{HAULAWAY_NOT_QUALIFY}</Alert>}
            </>
          )}
        {showLoadingSkeleton
          ? <LoadingSkeleton />
          : (
            <>
              {protectionItems?.length > 0
                ? (
                  <DropDownButton
                    type="protection"
                    userSelection={userSelections.protection}
                    availableItems={protectionItems}
                    onClick={setDrawerPageType}
                    isBopisAndPickup={isBopisAndPickup}
                  />
                )
                : <Alert status="warning">{PROTECTION_EMPTY}</Alert>}
            </>
          )}
      </TileGroup>
      <React.Fragment key="psdrawer">
        <Drawer
          open={!!drawerPageType}
          onClose={() => setDrawerPageType('')}
        >
          <DrawerHeader
            variant="sticky"
            title={drawerTitle}
            onClose={() => setDrawerPageType('')}
          />
          <DrawerBody>
            <QueryProvider cacheKey="PartsAndServices">
              {drawerPageType && (
                <PartsAndServices
                  addOnsSelections={widgetAddOnsSelections}
                  deliveryZip={deliveryZip}
                  disableHeader
                  disableNavigation
                  disableUpdateCart
                  itemIds={[itemId]}
                  lineItemIds={[lineItemId]}
                  onClose={() => setDrawerPageType('')}
                  onSubmit={getUserSelections}
                  pageType={drawerPageType}
                  useWidgetSave
                  confirmationEnabled={false}
                />
              )}
            </QueryProvider>
          </DrawerBody>
        </Drawer>
      </React.Fragment>
    </div>
  );
};

PartsAndServicesDropDownContainer.propTypes = {
  itemId: PropTypes.string.isRequired,
  lineItemId: PropTypes.string.isRequired,
  deliveryZip: PropTypes.string.isRequired,
  selectedAddOns: PropTypes.object.isRequired,
  setSelectedAddOns: PropTypes.func.isRequired,
  productInfo: PropTypes.object.isRequired,
  isBopis: PropTypes.bool
};

PartsAndServicesDropDownContainer.dataModel = partsAndServicesDataModel;
PartsAndServicesDropDownContainer.displayName = 'PartsAndServicesDropDownContainer';
